import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { ShoppingCart, Megaphone, Radio, Tv, FileText, Flag } from 'lucide-react';

const OFFLINE_CATS = [
  {
    id: 'buiten', label: 'Buitenreclame', icon: Megaphone,
    products: [
      { name: 'Billboard Marketing', price: 'Offerte', desc: 'Grootformaat reclame op drukke locaties.' },
      { name: 'Abri Reclame', price: 'Offerte', desc: 'Bushokje en abri reclame in uw regio.' },
      { name: 'Gevelreclame', price: 'Offerte', desc: 'Professionele gevel- en lichtbakreclame.' },
      { name: 'Reclameborden Langs Wegen', price: 'Offerte', desc: 'Borden op strategische locaties.' },
      { name: 'Spandoeken & Bouwborden', price: 'v.a. €199', desc: 'Tijdelijke en permanente buitenreclame.' },
    ]
  },
  {
    id: 'sport', label: 'Sportreclame & Sponsoring', icon: Flag,
    products: [
      { name: 'Reclameborden Voetbalveld', price: 'v.a. €299/seizoen', desc: 'Zichtbaar bij lokale voetbalverenigingen.' },
      { name: 'Sponsorborden Sporthal', price: 'v.a. €249/seizoen', desc: 'Branding bij indoor sportlocaties.' },
      { name: 'Boarding Sportvelden', price: 'Offerte', desc: 'Boarding langs alle sportvelden in uw regio.' },
      { name: 'Club Sponsoring Pakket', price: 'Offerte', desc: 'Complete sponsoringoplossing bij sportvereniging.' },
      { name: 'Lokale Sportreclamecampagne', price: 'Offerte', desc: 'Gecombineerde sportcampagne in uw regio.' },
    ]
  },
  {
    id: 'radio', label: 'Radio & Audio', icon: Radio,
    products: [
      { name: 'Lokale Radiocampagne', price: 'v.a. €499', desc: 'Radiospot op lokale radiozenders.' },
      { name: 'Regionale Radiocampagne', price: 'Offerte', desc: 'Bereik uw regio via regionale radio.' },
      { name: 'Audiospot Productie', price: 'v.a. €299', desc: 'Professionele opname en productie.' },
      { name: 'Sponsorvermelding Radio', price: 'Offerte', desc: 'Merkvermelding in programma\'s.' },
    ]
  },
  {
    id: 'tv', label: 'Televisie & Video', icon: Tv,
    products: [
      { name: 'Regionale TV Campagne', price: 'Offerte', desc: 'TV-commercial op regionale omroepen.' },
      { name: 'Videocommercial Productie', price: 'v.a. €1.499', desc: 'Professionele commercial productie.' },
      { name: 'TV Sponsorvermelding', price: 'Offerte', desc: 'Merkvermelding in TV-programma.' },
    ]
  },
  {
    id: 'drukwerk', label: 'Drukwerk & Promotie', icon: FileText,
    products: [
      { name: 'Flyercampagne', price: 'v.a. €199', desc: 'Professionele flyers ontworpen en gedrukt.' },
      { name: 'Brochure Pakket', price: 'v.a. €349', desc: 'Professionele brochures voor uw bedrijf.' },
      { name: 'Direct Mail Campagne', price: 'Offerte', desc: 'Gerichte verzending naar uw doelgroep.' },
      { name: 'Visitekaartjes', price: 'v.a. €49', desc: 'Professionele visitekaartjes.' },
      { name: 'Voertuigbestickering', price: 'Offerte', desc: 'Rijdende reclame voor uw wagenpark.' },
    ]
  },
];

export default function OfflineMarketing() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('buiten');
  const activeCat = OFFLINE_CATS.find(c => c.id === activeTab);

  const handleContact = (productName) => {
    toast.success(`Aanvraag ontvangen voor: ${productName}. We nemen binnen 24 uur contact op.`);
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-[#0f172a]">Offline Marketing</h1>
        <p className="text-[#64748b] text-sm mt-1">Vergroot uw lokale bekendheid met professionele offline campagnes</p>
      </div>

      {/* Hero */}
      <div className="bg-[#0f172a] rounded-2xl p-6 mb-5 grid md:grid-cols-3 gap-4">
        {[['Lokale Zichtbaarheid', 'Bereik klanten in uw regio op alle mogelijke plekken.'],
          ['Merkbekendheid', 'Bouw een sterk lokaal merk op via meerdere kanalen.'],
          ['Omnichannel', 'Combineer online en offline voor maximale impact.']].map(([t, d]) => (
          <div key={t}>
            <p className="text-[#D4A017] font-bold text-sm mb-1">{t}</p>
            <p className="text-[#94a3b8] text-xs">{d}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {OFFLINE_CATS.map(c => {
          const Icon = c.icon;
          return (
            <button key={c.id} onClick={() => setActiveTab(c.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all ${activeTab === c.id ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] border border-gray-200 hover:border-[#0f172a]'}`}>
              <Icon size={12} /> {c.label}
            </button>
          );
        })}
      </div>

      {activeCat && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeCat.products.map(p => (
            <div key={p.name} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-all">
              <h3 className="font-bold text-[#0f172a] text-sm mb-2">{p.name}</h3>
              <p className="text-[#64748b] text-xs mb-3 leading-relaxed">{p.desc}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#D4A017] font-bold text-sm">{p.price}</span>
                <button onClick={() => handleContact(p.name)}
                  className="flex items-center gap-1.5 bg-[#0f172a] text-white font-bold text-xs px-3 py-2 rounded-xl hover:bg-[#1e293b] transition-all">
                  <ShoppingCart size={11} /> {p.price === 'Offerte' ? 'Aanvragen' : 'Bestel'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
